import { Observable, Subscription, defer, map } from 'rxjs';
import { FeatureCollection } from 'geojson';
import { FeedFilterService } from '../../../services/feed-filter.service';
import { ViewportWfsService } from './viewport-wfs.service';
import { parseFeedResultCount } from '../../../services/feed-result-count';

export interface LegendFilters {
  classFilter?: { type: string; value: any };
  vesselLengthFilter?: { value: number; isGreater: boolean };
}

export interface LegendSource {
  storageKey: string;
  layerName: string;
  baseCql: string | null;
  filterKey?: string;
  feedType: string;
}

export type LegendSummary =
  | { status: 'loading' | 'unavailable' }
  | { status: 'ready'; total: number; classCounts?: Record<string, number>; original?: true };

/** Full-feed summaries have no viewport inputs and never expose geometries to the map. */
export class FeedLegendSummaryController {
  private readonly sessions = new Map<string, {
    source: LegendSource;
    key?: string;
    state?: LegendSummary;
    request?: Subscription;
    cache: Map<string, LegendSummary>;
  }>();

  constructor(
    private readonly wfs: ViewportWfsService,
    private readonly filters: FeedFilterService,
    private readonly publish: (storageKey: string) => void
  ) {}

  /** Seed only from the original feed/scene metadata, never a viewport or page size. */
  register(source: LegendSource, originalCount?: unknown): void {
    this.remove(source.storageKey);
    const session = { source: { ...source }, cache: new Map<string, LegendSummary>() };
    const total = parseFeedResultCount(originalCount);
    if (total !== null) {
      const key = JSON.stringify([session.source, {}]);
      const state: LegendSummary = { status: 'ready', total, original: true };
      session.cache.set(key, state);
    }
    this.sessions.set(source.storageKey, session);
  }

  state(storageKey: string): LegendSummary | undefined {
    return this.sessions.get(storageKey)?.state;
  }

  refresh(storageKey: string, filters: LegendFilters): void {
    const session = this.sessions.get(storageKey);
    if (!session) return;
    // Snapshot filters: later UI mutations must not change an in-flight calculation.
    const snapshot: LegendFilters = JSON.parse(JSON.stringify(filters));
    const key = JSON.stringify([session.source, snapshot]);
    if (session.key === key) return;
    session.request?.unsubscribe();
    session.key = key;
    const cached = session.cache.get(key);
    session.state = cached ?? { status: 'loading' };
    this.publish(storageKey);
    if (cached) return;

    session.request = defer(() => this.calculate(session.source, snapshot)).subscribe({
      next: (state) => {
        if (this.sessions.get(storageKey) !== session || session.key !== key) return;
        session.state = state;
        if (state.status === 'ready') session.cache.set(key, state);
        this.publish(storageKey);
      },
      error: () => {
        if (this.sessions.get(storageKey) !== session || session.key !== key) return;

        session.state = { status: 'unavailable' };
        this.publish(storageKey);
      },
    });
  }

  remove(storageKey: string): void {
    this.sessions.get(storageKey)?.request?.unsubscribe();
    this.sessions.delete(storageKey);
  }

  clear(): void {
    Array.from(this.sessions.keys()).forEach((key) => this.remove(key));
  }

  private calculate(source: LegendSource, filters: LegendFilters): Observable<LegendSummary> {
    // Client normalization (aliases, units, missing lengths and trimmed classes) is
    // not proven equivalent to a GeoServer expression. Reuse it exactly, without BBOX.
    return this.wfs.loadAllFeatures(source.layerName, source.baseCql).pipe(map((fc: FeatureCollection) => {
      const base = source.filterKey
        ? fc.features.filter((feature) => feature.properties?.['class'] != source.filterKey)
        : fc.features;
      const features = this.filters.filterFeatures(base, source.feedType, filters.classFilter, filters.vesselLengthFilter);
      return {
        status: 'ready' as const,
        total: features.length,
        classCounts: this.filters.getClassCountsForLegend(source.feedType, features),
      };
    }));
  }
}
